import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/components/feature/AdminAuthContext';
import { getSupabase } from '@/lib/supabase';
import Logo from '@/components/feature/Logo';

interface AppRow {
  id: string;
  reference_number: string;
  client_full_name: string;
  client_phone: string;
  client_email: string;
  country_of_travel: string;
  visa_type: string;
  current_stage: number;
  final_decision: string;
  application_type: string;
  submitted_at: string;
}

export default function AllApplications() {
  const { staffName, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState<AppRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [countryFilter, setCountryFilter] = useState('All');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fetchApps = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const supabase = getSupabase();
      const { data, error: fetchErr } = await supabase
        .from('applications')
        .select('id, reference_number, client_full_name, client_phone, client_email, country_of_travel, visa_type, application_type, current_stage, final_decision, submitted_at')
        .order('submitted_at', { ascending: false });

      if (fetchErr) throw fetchErr;
      setApps(data || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const filtered = apps.filter((a) => {
    const q = search.toLowerCase();
    const matchesSearch = !q ||
      a.client_full_name.toLowerCase().includes(q) ||
      a.reference_number.toLowerCase().includes(q) ||
      a.country_of_travel.toLowerCase().includes(q) ||
      a.client_email.toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'All' ||
      (statusFilter === 'approved' && a.final_decision === 'approved') ||
      (statusFilter === 'declined' && a.final_decision === 'declined') ||
      (statusFilter === 'pending' && a.final_decision === 'pending');
    const matchesCountry = countryFilter === 'All' || a.country_of_travel === countryFilter;
    return matchesSearch && matchesStatus && matchesCountry;
  });

  const countries = [...new Set(apps.map((a) => a.country_of_travel))].sort();

  const getStatusClass = (decision: string, stage: number) => {
    if (decision === 'approved') return 'bg-green-100 text-green-700';
    if (decision === 'declined') return 'bg-red-100 text-red-700';
    if (stage <= 2) return 'bg-secondary-200 text-foreground-600';
    if (stage <= 4) return 'bg-amber-100 text-amber-700';
    if (stage <= 6) return 'bg-primary-100 text-primary-700';
    return 'bg-accent-100 text-accent-700';
  };

  const getStatusLabel = (decision: string, stage: number) => {
    if (decision === 'approved') return 'Approved';
    if (decision === 'declined') return 'Declined';
    const labels: Record<number, string> = { 1: 'Received', 2: 'Docs Collected', 3: 'Under Review', 4: 'Verified', 5: 'Appt Set', 6: 'Submitted', 7: 'Processing', 8: 'Info Req.', 9: 'Decision Rcvd', 10: 'Finalizing', 11: 'Completed' };
    return labels[stage] || 'Pending';
  };

  return (
    <div className="min-h-screen bg-background-50">
      <header className="bg-primary-900 sticky top-0 z-40">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-14 md:h-16">
            <div className="flex items-center gap-3">
              <Logo variant="light" className="w-[130px] md:w-[170px]" />
              <span className="hidden sm:block font-heading font-bold text-sm md:text-base text-background-50 whitespace-nowrap">PVS Admin</span>
            </div>
            <div className="hidden md:flex items-center gap-1">
              <Link to="/admin" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Dashboard</Link>
              <Link to="/admin/appointments" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Appointments</Link>
              <Link to="/admin/applications" className="px-3 py-2 text-sm font-medium text-background-50 bg-background-50/10 rounded-md whitespace-nowrap cursor-pointer">All Applications</Link>
              <Link to="/admin/reports" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Reports</Link>
              <Link to="/admin/settings" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Settings</Link>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-background-50/80 text-sm">{staffName}</span>
              <button onClick={handleLogout} className="px-3 py-1.5 bg-background-50/10 text-background-50 text-xs font-medium rounded-md border border-background-50/20 hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer">Logout</button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-8 h-8 flex items-center justify-center text-background-50 cursor-pointer">
                <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden pb-3 flex flex-col gap-1">
              <Link to="/admin" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">Dashboard</Link>
              <Link to="/admin/appointments" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">Appointments</Link>
              <Link to="/admin/applications" className="px-3 py-2 text-sm font-medium text-background-50 bg-background-50/10 rounded-md whitespace-nowrap cursor-pointer">All Applications</Link>
              <Link to="/admin/reports" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">Reports</Link>
              <Link to="/admin/settings" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">Settings</Link>
            </div>
          )}
        </div>
      </header>

      <main className="py-8 md:py-10">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="mb-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">All Applications</h2>
            <p className="text-sm text-foreground-600 mt-1">{filtered.length} application{filtered.length !== 1 ? 's' : ''} found</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3 mb-6 flex items-center gap-2">
              <i className="ri-error-warning-line"></i> {error}
              <button onClick={fetchApps} className="ml-auto text-xs font-medium underline whitespace-nowrap cursor-pointer">Retry</button>
            </div>
          )}

          <div className="bg-background-50 border border-secondary-200 rounded-lg p-4 md:p-5 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Search</label>
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, reference..." className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer">
                  <option value="All">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="declined">Declined</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Country</label>
                <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)} className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer">
                  <option value="All">All Countries</option>
                  {countries.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </div>
              <div className="flex items-end">
                <button onClick={() => { setSearch(''); setStatusFilter('All'); setCountryFilter('All'); }} className="w-full px-4 py-2 bg-secondary-100 text-foreground-700 text-sm font-medium rounded-md hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer">Clear Filters</button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
              <p className="text-sm text-foreground-500 mt-3">Loading applications...</p>
            </div>
          ) : (
            <>
              <div className="hidden lg:block bg-background-50 border border-secondary-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-secondary-200 bg-secondary-50">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 whitespace-nowrap">Reference</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 whitespace-nowrap">Client Name</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 whitespace-nowrap">Phone</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 whitespace-nowrap">Email</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 whitespace-nowrap">Country</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 whitespace-nowrap">Visa Type</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 whitespace-nowrap">Date</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 whitespace-nowrap">Type</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 whitespace-nowrap">Status</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-600 whitespace-nowrap">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((app) => (
                        <tr key={app.id} className="border-b border-secondary-100 hover:bg-secondary-50 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-primary-700 whitespace-nowrap">{app.reference_number}</td>
                          <td className="px-4 py-3 text-sm text-foreground-800 whitespace-nowrap font-medium">{app.client_full_name}</td>
                          <td className="px-4 py-3 text-sm text-foreground-600 whitespace-nowrap">{app.client_phone}</td>
                          <td className="px-4 py-3 text-sm text-foreground-600 whitespace-nowrap">{app.client_email}</td>
                          <td className="px-4 py-3 text-sm text-foreground-600 whitespace-nowrap">{app.country_of_travel}</td>
                          <td className="px-4 py-3 text-sm text-foreground-600 whitespace-nowrap">{app.visa_type}</td>
                          <td className="px-4 py-3 text-sm text-foreground-600 whitespace-nowrap">{app.submitted_at?.split('T')[0]}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              app.application_type === 'office_appointment' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'
                            }`}>
                              {app.application_type === 'office_appointment' ? 'Office' : 'Home'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClass(app.final_decision, app.current_stage)}`}>
                              {getStatusLabel(app.final_decision, app.current_stage)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <Link to={`/admin/client/${app.id}`} className="inline-block px-4 py-1.5 bg-primary-500 text-background-50 text-xs font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer">View / Update</Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filtered.length === 0 && <div className="text-center py-12 text-sm text-foreground-500">No applications found.</div>}
              </div>

              <div className="lg:hidden space-y-3">
                {filtered.map((app) => (
                  <div key={app.id} className="bg-background-50 border border-secondary-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-primary-700">{app.reference_number}</span>
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClass(app.final_decision, app.current_stage)}`}>{getStatusLabel(app.final_decision, app.current_stage)}</span>
                    </div>
                    <p className="text-sm font-medium text-foreground-900">{app.client_full_name}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{app.country_of_travel} · {app.visa_type} Visa</p>
                    <p className="text-xs text-foreground-500">{app.client_phone} · {app.client_email}</p>
                    <div className="mt-3">
                      <Link to={`/admin/client/${app.id}`} className="inline-block w-full text-center px-4 py-2 bg-primary-500 text-background-50 text-xs font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer">View / Update</Link>
                    </div>
                  </div>
                ))}
                {filtered.length === 0 && <div className="text-center py-12 text-sm text-foreground-500">No applications found.</div>}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}